import { pushRoute } from '../../lib/router.js';
import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { loadingContent } from '../../components/EmptyStates.jsx';
import {
  deleteChangeImpact,
  retrieveImpactedEntities,
  searchChangeImpacts,
  selectImpactedEntities,
  selectRetrieveImpactedEntitiesState,
  selectSearchChangeImpactsState,
  selectSearchResults,
} from '../../modules/changeImpacts.js';
import ChangeImpact from '../../components/projects/ChangeImpact/index.jsx';
import {
  retrieveChangeImpactTypes,
  selectChangeImpactTypesWithArchived,
} from '../../modules/changeImpactTypes.js';
import {
  retrieveProject,
  selectProject,
  selectRetrieveProjectState,
} from '../../modules/projects.js';
import Empty from '../../components/Empty.jsx';
import { useFetchFile } from '../../hooks/useFetchFile.js';
import {
  retrieveProjectStatuses,
  selectProjectStatusesWithArchived,
} from '../../modules/projectStatuses.js';
import { selectUserPermissions } from '../../modules/auth.js';
import {
  retrievePortfolios,
  selectPortfolio,
} from '../../modules/portfolio.js';
import { sortBy } from '../reports/utils/index.js';
import { resetFilters } from '../../components/projects/ChangeImpact/utils/index.js';
import ImportDataProgressContainer from '../ImportDataProgressContainer.jsx';
import ProjectHeader from '../../components/headers/PageHeaders/Project/ProjectHeader.jsx';

const mapStateToProps = (state, ownProps) => {
  const { projectId } = ownProps;
  const project = selectProject(projectId)(state);
  const projectStatuses = selectProjectStatusesWithArchived(state);
  const impactedEntities = selectImpactedEntities(projectId)(state);
  const retrieveSuccess =
    selectRetrieveProjectState(state).success &&
    selectRetrieveImpactedEntitiesState(state).success;

  const { query, error, pending, success } =
    selectSearchChangeImpactsState(state);

  return {
    retrieveSuccess,
    projectId,
    project,
    impactedEntities,
    projectStatuses,
    searchQuery: query,
    searchPending: pending,
    searchSuccess: success,
    searchError: error,
    searchResults: selectSearchResults(state),
    changeImpactTypes: selectChangeImpactTypesWithArchived(state),
    apiUrl: state.config.apiUrl,
    permissions: selectUserPermissions(state),
    portfolio: project?.portfolioId
      ? selectPortfolio(project.portfolioId)(state)
      : null,
  };
};

const mapDispatchToProps = {
  retrieveProject,
  retrieveProjectStatuses,
  retrieveChangeImpactTypes,
  searchChangeImpacts,
  retrieveImpactedEntities,
  selectRetrieveImpactedEntitiesState,
  retrievePortfolios,
  deleteChangeImpact,
};

function ChangeImpactsContainer(props) {
  const {
    projectId,
    project,
    portfolio,
    impactedEntities,
    impactedGroups,
    projectStatuses,
    deleteChangeImpact,
    changeImpactTypes,
    retrieveProject,
    retrieveImpactedEntities,
    searchChangeImpacts,
    retrieveProjectStatuses,
    retrieveChangeImpactTypes,
    retrieveSuccess,
    searchSuccess,
    query,
    searchResults,
    apiUrl,
    permissions,
    retrievePortfolios,
  } = props;
  const { loading, downloadBlob } = useFetchFile();
  const [viewType, setViewType] = useState('pie');
  const [sortType, setSortType] = useState(sortBy[0]);

  const setReportType = (type) => {
    resetFilters();
    setViewType(type);
  };

  const pieTypeRef = useRef(null);
  const piePerceptionRef = useRef(null);
  const pieScaleRef = useRef(null);

  const onDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this change impact?')) {
      const submitPromise = deleteChangeImpact(id);

      submitPromise.then(() =>
        pushRoute('projects/change-impacts', { projectId })
      );
    }
  };

  const onDownloadReport = () => {
    const body =
      viewType === 'pie'
        ? JSON.stringify({
            type_chart_html: pieTypeRef.current.outerHTML,
            perception_chart_html: piePerceptionRef.current.outerHTML,
            scale_chart_html: pieScaleRef.current.outerHTML,
          })
        : null;

    downloadBlob(
      apiUrl,
      `projects/${projectId}/change_impacts/pdf`,
      query,
      'change-impacts-report',
      'pdf',
      'POST',
      body
    );
  };

  useEffect(() => {
    retrieveProject(projectId, false);
    retrieveChangeImpactTypes();
    retrieveImpactedEntities(projectId);
    retrieveProjectStatuses();
    retrievePortfolios();
  }, []);

  useEffect(() => {
    searchChangeImpacts(projectId, query);
  }, [query]);

  if (!impactedEntities || !project || !project.id) {
    return <Empty simplified content={loadingContent} />;
  }

  return (
    retrieveSuccess && (
      <div className="relative">
        <ImportDataProgressContainer
          projectId={project.id}
          status={project.processingChangeImpactUpload}
        />
        <ProjectHeader project={project} />
        <ChangeImpact
          query={query}
          project={project}
          portfolio={portfolio}
          searchSuccess={searchSuccess}
          results={searchResults}
          impactedEntities={impactedEntities}
          impactedGroups={impactedGroups}
          projectStatuses={projectStatuses}
          onDownloadReport={onDownloadReport}
          permissions={permissions}
          isLoading={loading}
          pieTypeRef={pieTypeRef}
          pieScaleRef={pieScaleRef}
          piePerceptionRef={piePerceptionRef}
          changeImpactTypes={changeImpactTypes}
          viewType={viewType}
          sortType={sortType}
          setSortType={setSortType}
          setViewType={setReportType}
          onDelete={onDelete}
        />
      </div>
    )
  );
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ChangeImpactsContainer);
